import type { ReactNode } from "react";
import "./Avatars.css";

/**
 * Avatares de los empleados virtuales: ilustraciones propias en SVG (no hay
 * imagenes de terceros). Cada uno tiene su personaje y un objeto animado que
 * cuenta que hace -burbujas de chat, planilla con tildes, grafico que sube,
 * ondas de voz, linea de tiempo de video-. El movimiento vive en Avatars.css
 * (clases av-*) y se apaga con prefers-reduced-motion.
 */

const INK = "#1d1d22";

function Frame({ variant, children }: { variant: string; children: ReactNode }) {
  return (
    <svg
      className={`av av--${variant}`}
      viewBox="0 0 240 240"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

function Neck({ shade }: { shade: string }) {
  return <rect x="103" y="148" width="34" height="38" rx="10" fill={shade} />;
}

function Shoulders({ fill }: { fill: string }) {
  return <path d="M24 240 C24 198 66 176 120 176 C174 176 216 198 216 240 Z" fill={fill} />;
}

function Head({ skin }: { skin: string }) {
  return (
    <>
      <circle cx="70" cy="114" r="9" fill={skin} />
      <circle cx="170" cy="114" r="9" fill={skin} />
      <ellipse cx="120" cy="108" rx="50" ry="54" fill={skin} />
    </>
  );
}

/** Cejas, ojos (que parpadean), cachetes y sonrisa: igual en todos. */
function Face() {
  return (
    <>
      <path d="M92 99 Q101 93 111 98" stroke={INK} strokeWidth="3" strokeLinecap="round" opacity=".75" />
      <path d="M129 98 Q139 93 148 99" stroke={INK} strokeWidth="3" strokeLinecap="round" opacity=".75" />
      <g className="av-eyes">
        <ellipse cx="102" cy="114" rx="5" ry="6.5" fill={INK} />
        <ellipse cx="138" cy="114" rx="5" ry="6.5" fill={INK} />
        <circle cx="103.8" cy="111.8" r="1.8" fill="#fff" />
        <circle cx="139.8" cy="111.8" r="1.8" fill="#fff" />
      </g>
      <circle cx="88" cy="130" r="8" fill="#ff7a8a" opacity=".32" />
      <circle cx="152" cy="130" r="8" fill="#ff7a8a" opacity=".32" />
      <path d="M108 133 Q120 145 132 133" stroke={INK} strokeWidth="3.5" strokeLinecap="round" />
    </>
  );
}

/** Community Manager: burbuja de chat escribiendo + reaccion con corazon. */
export function CommunityManagerAvatar() {
  const hair = "#4a2a5c";
  return (
    <Frame variant="cm">
      <g className="av-figure">
        <Neck shade="#e3ad84" />
        <Shoulders fill="#cb5aa0" />
        <path d="M100 176 Q120 204 140 176 Z" fill="#e3ad84" />
        <g className="av-headgroup">
          <path
            d="M64 112 C56 58 88 38 120 38 C152 38 184 58 176 112 C182 146 180 168 168 184 C150 190 90 190 72 184 C60 168 58 146 64 112 Z"
            fill={hair}
          />
          <Head skin="#f2c6a0" />
          <Face />
          <path
            d="M68 104 C68 70 96 54 124 56 C152 58 172 76 172 104 C158 86 134 78 112 82 C92 86 78 94 68 104 Z"
            fill={hair}
          />
          <path d="M98 64 C110 58 126 58 140 62" stroke="#7a4a92" strokeWidth="3" strokeLinecap="round" opacity=".7" />
        </g>
      </g>

      <g className="av-prop av-prop--a">
        <rect x="164" y="30" width="62" height="38" rx="15" fill="#fff" />
        <path d="M180 67 L174 82 L194 67 Z" fill="#fff" />
        <circle className="av-dot" cx="182" cy="49" r="4.2" fill="#cb5aa0" />
        <circle className="av-dot" cx="195" cy="49" r="4.2" fill="#cb5aa0" style={{ animationDelay: ".2s" }} />
        <circle className="av-dot" cx="208" cy="49" r="4.2" fill="#cb5aa0" style={{ animationDelay: ".4s" }} />
      </g>
      <g className="av-prop av-prop--b">
        <circle cx="36" cy="82" r="22" fill="#cb5aa0" />
        <path
          d="M36 94 C23 85 24 70 32 70 C35 70 36 72 36 74 C36 72 37 70 40 70 C48 70 49 85 36 94 Z"
          fill="#fff"
        />
      </g>
    </Frame>
  );
}

/** Administrativo: lentes + planilla que se va tildando. */
export function AdministrativoAvatar() {
  const skin = "#e8b88f";
  const shade = "#d39f73";
  return (
    <Frame variant="admin">
      <g className="av-figure">
        <Neck shade={shade} />
        <Shoulders fill="#2fa89a" />
        <path d="M106 176 L120 196 L134 176 Z" fill={shade} />
        <path d="M96 176 L116 202 L120 196 L106 176 Z" fill="#fff" />
        <path d="M144 176 L124 202 L120 196 L134 176 Z" fill="#fff" />
        <g className="av-headgroup">
          <Head skin={skin} />
          <Face />
          <path
            d="M69 104 C66 66 92 48 122 48 C154 48 174 68 171 104 C164 84 144 72 120 72 C96 72 76 84 69 104 Z"
            fill="#2b2118"
          />
          <rect x="86" y="102" width="32" height="24" rx="9" stroke={INK} strokeWidth="3" fill="rgba(255,255,255,.14)" />
          <rect x="122" y="102" width="32" height="24" rx="9" stroke={INK} strokeWidth="3" fill="rgba(255,255,255,.14)" />
          <path d="M118 112 H122" stroke={INK} strokeWidth="3" strokeLinecap="round" />
        </g>
      </g>

      <g className="av-prop av-prop--a">
        <rect x="166" y="100" width="58" height="78" rx="10" fill="#fff" />
        <rect x="184" y="93" width="22" height="13" rx="5" fill={INK} />
        {[0, 1, 2].map((row) => (
          <g key={row}>
            <path
              className="av-check"
              d={`M176 ${124 + row * 18} l4 4 l8 -9`}
              stroke="#2fa89a"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ animationDelay: `${row * 0.9}s` }}
            />
            <rect x="196" y={122 + row * 18} width="22" height="5" rx="2.5" fill="#d8dde3" />
          </g>
        ))}
      </g>
    </Frame>
  );
}

/** Asistente comercial: saco y corbata + grafico de barras que crece. */
export function ComercialAvatar() {
  const skin = "#c68a62";
  const shade = "#b0764f";
  return (
    <Frame variant="sales">
      <g className="av-figure">
        <Neck shade={shade} />
        <Shoulders fill="#243b7a" />
        <path d="M100 177 L120 212 L140 177 Z" fill="#fff" />
        <path d="M113 178 L127 178 L131 216 L120 228 L109 216 Z" fill="#ccc545" />
        <rect x="112" y="176" width="16" height="12" rx="3" fill="#b3ac35" />
        <g className="av-headgroup">
          <Head skin={skin} />
          <Face />
          <path
            d="M70 100 C68 64 98 46 128 48 C158 50 174 70 170 100 C162 80 138 68 108 72 C90 76 76 86 70 100 Z"
            fill="#15110e"
          />
        </g>
      </g>

      <g className="av-prop av-prop--a">
        <rect x="10" y="32" width="78" height="64" rx="13" fill="#fff" />
        {[14, 24, 34, 46].map((h, i) => (
          <rect
            key={h}
            className="av-bar"
            x={22 + i * 15}
            y={86 - h}
            width="10"
            height={h}
            rx="3"
            fill={i === 3 ? "#22c55e" : "#3a6df0"}
            style={{ animationDelay: `${i * 0.18}s` }}
          />
        ))}
        <circle cx="80" cy="36" r="10" fill="#22c55e" />
        <path d="M80 41 V31 M76 35 L80 31 L84 35" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
      </g>
    </Frame>
  );
}

/** Recepcionista IA: auricular con microfono + ondas de voz + turno confirmado. */
export function RecepcionistaAvatar() {
  const skin = "#f6d2b8";
  const shade = "#e7b997";
  const hair = "#6b3a1c";
  return (
    <Frame variant="recep">
      <g className="av-figure">
        <Neck shade={shade} />
        <Shoulders fill="#f2a51b" />
        <path d="M100 177 L120 206 L140 177 Z" fill="#fff" />
        <g className="av-headgroup">
          <circle cx="120" cy="40" r="20" fill={hair} />
          <Head skin={skin} />
          <Face />
          <path
            d="M68 106 C64 66 92 50 120 50 C150 50 176 68 172 106 C162 84 142 74 120 74 C98 74 78 84 68 106 Z"
            fill={hair}
          />
          <path d="M70 112 C66 54 174 54 170 112" stroke="#2a2a30" strokeWidth="6" strokeLinecap="round" />
          <rect x="60" y="100" width="16" height="32" rx="8" fill="#2a2a30" />
          <rect x="164" y="100" width="16" height="32" rx="8" fill="#2a2a30" />
          <path d="M172 130 C178 152 160 158 145 150" stroke="#2a2a30" strokeWidth="4" strokeLinecap="round" />
          <circle cx="141" cy="149" r="6" fill="#ccc545" />
        </g>
      </g>

      <path className="av-wave" d="M190 104 Q202 118 190 132" stroke="#f2a51b" strokeWidth="4" strokeLinecap="round" />
      <path
        className="av-wave"
        d="M204 94 Q222 118 204 142"
        stroke="#f2a51b"
        strokeWidth="4"
        strokeLinecap="round"
        style={{ animationDelay: ".35s" }}
      />

      <g className="av-prop av-prop--b">
        <rect x="10" y="34" width="62" height="60" rx="12" fill="#fff" />
        <path d="M10 52 V46 a12 12 0 0 1 12 -12 H60 a12 12 0 0 1 12 12 V52 Z" fill="#f2a51b" />
        {[0, 1, 2].map((i) => (
          <circle key={i} cx={24 + i * 14} cy="66" r="3" fill="#d8dde3" />
        ))}
        <circle cx="56" cy="80" r="11" fill="#22c55e" />
        <path
          className="av-check"
          d="M50 80 l4 4 l8 -9"
          stroke="#fff"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
    </Frame>
  );
}

/** Editor de video: gorro + auriculares + linea de tiempo con cabezal que avanza. */
export function EditorVideoAvatar() {
  const skin = "#8d5a3b";
  const shade = "#774a2f";
  return (
    <Frame variant="video">
      <g className="av-figure">
        <Neck shade={shade} />
        <Shoulders fill="#4b2fb0" />
        <path d="M82 178 C94 204 146 204 158 178 C146 192 94 192 82 178 Z" fill="#3a2390" />
        <path d="M108 194 V220 M132 194 V220" stroke="#e9e6ff" strokeWidth="3" strokeLinecap="round" />
        <g className="av-headgroup">
          <Head skin={skin} />
          <Face />
          <path d="M66 98 C62 52 94 34 120 34 C146 34 178 52 174 98 Z" fill="#ccc545" />
          <rect x="62" y="86" width="116" height="20" rx="10" fill="#b3ac35" />
          <path d="M63 106 C58 28 182 28 177 106" stroke={INK} strokeWidth="6" strokeLinecap="round" />
          <rect x="52" y="100" width="22" height="36" rx="11" fill={INK} />
          <rect x="166" y="100" width="22" height="36" rx="11" fill={INK} />
        </g>
      </g>

      <g className="av-prop av-prop--a">
        <rect x="156" y="30" width="72" height="52" rx="12" fill="#fff" />
        <path className="av-play" d="M184 40 L206 52 L184 64 Z" fill="#4b2fb0" />
        <rect x="166" y="72" width="52" height="4" rx="2" fill="#d8dde3" />
        <circle className="av-playhead" cx="166" cy="74" r="4.5" fill="#cb5aa0" />
      </g>
    </Frame>
  );
}
